/*
 * Cockpit — the tiered Operator Cockpit over the fail-closed provisioning CLI
 * (design §8). It is a render-only HONEST surface: the browser cannot present a
 * client cert, so every Tier-1/2 privileged action is disabled-with-explanation
 * unless reached behind the mTLS-terminating proxy. The view never fabricates a
 * success it cannot back from live data, and it surfaces the mTLS boundary as a
 * first-class affordance rather than hiding it.
 *
 * Four sections, mapping the tier model:
 *   1. Observe (Tier-0)   — per-plane state from /status + the pki_ready stage
 *                           flag; offline-honest when the backend 502s.
 *   2. Manifest firewall  — the declare-zone as a read-only summary with the
 *      (Tier-1 affordance)  declare / generate / pin firewall chips; the THP as
 *                           a read-only <Hash>; the pin-zone as presence chips.
 *   3. Ceremony wizard    — Tier-2 verify-pins: paste two PUBLIC pins, see the
 *      (Tier-2)             render-don't-execute transcript (no seed, no minting).
 *   4. Deploy + exchange   — the fail-closed Deploy gate (both external pins
 *      (Tier-1)             present AND pki_ready) and the step-9 four-plane
 *                           exchange visualizer.
 *
 * Reuses only existing Sovereign-Night components (PlaneState, VerifyBadge,
 * Hash) and the existing token/class vocabulary. No new design token.
 */

import { useEffect, useMemo, useState } from "react";
import {
  getCockpitManifest,
  getCockpitStatus,
  verifyPins,
  type CockpitManifest,
  type CockpitStatus,
  type VerifyPinsResult,
} from "../api/cockpit";
import { PlaneState } from "../components/PlaneState";
import { SpecRef } from "../components/SpecRef";
import { VerifyBadge } from "../components/VerifyBadge";
import { Hash } from "../components/Hash";
import type { Role, VerifyState } from "../api/types";

/* The four planes the step-9 exchange traverses, in hop order (design §10). */
const EXCHANGE_PLANES: { id: string; label: string; accent: string }[] = [
  { id: "consent", label: "Consent", accent: "var(--indigo)" },
  { id: "trust", label: "Trust", accent: "var(--teal)" },
  { id: "transport", label: "Transport", accent: "var(--ember)" },
  { id: "observability", label: "Audit", accent: "var(--gold)" },
];

/* Map a backend per-plane state string to a VerifyBadge verdict + colour. */
function planeVerdict(state: string): VerifyState {
  switch (state) {
    case "ready":
    case "healthy":
    case "up":
      return "verified";
    case "pending":
    case "starting":
      return "pending";
    case "down":
    case "offline":
    case "error":
      return "offline";
    default:
      return "unknown";
  }
}

export default function Cockpit({ role }: { role: Role }) {
  const [status, setStatus] = useState<CockpitStatus | null>(null);
  const [manifest, setManifest] = useState<CockpitManifest | null>(null);
  const [offline, setOffline] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [s, m] = await Promise.all([
          getCockpitStatus(),
          getCockpitManifest().catch(() => null),
        ]);
        if (alive) {
          setStatus(s);
          setManifest(m);
        }
      } catch (e) {
        if (alive) setOffline(e instanceof Error ? e.message : "cockpit backend unreachable");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // The fail-closed Deploy gate: BOTH external pins must be present (spec §8.2)
  // AND the node PKI must be established. The SPA reports the gate honestly; it
  // can never itself satisfy the mTLS leg (a browser holds no client cert).
  const pinsReady =
    !!manifest &&
    manifest.trust_anchors_present.witness_genesis_pin &&
    manifest.trust_anchors_present.audit_anchor_signer_thp;
  const deployEnabled = pinsReady && !!status?.pki_ready;

  if (offline) {
    return (
      <section>
        <Header />
        <PlaneState
          kind="offline"
          title="Cockpit backend not reachable"
          message={`The provisioning backend at /v1/cockpit is not up in this deployment (${offline}). Pre-PKI the panel is localhost render-only; the backend is wired at provision time, behind the mTLS-terminating proxy.`}
          specRef="Design §8"
          endpoints={[
            "GET /v1/cockpit/status",
            "GET /v1/cockpit/manifest",
            "POST /v1/cockpit/ceremony/verify-pins",
            "POST /v1/cockpit/deploy",
            "POST /v1/cockpit/exchange/run",
          ]}
        />
      </section>
    );
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <Header pkiReady={status?.pki_ready} />
      <ObserveSection status={status} />
      <ManifestFirewall manifest={manifest} pinsReady={pinsReady} />
      <CeremonyWizard pkiReady={status?.pki_ready} />
      <DeployAndExchange
        role={role}
        deployEnabled={deployEnabled}
        pinsReady={pinsReady}
        pkiReady={status?.pki_ready}
      />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Header                                                             */
/* ------------------------------------------------------------------ */

function Header({ pkiReady }: { pkiReady?: boolean }) {
  const stage = pkiReady === undefined ? "loading" : pkiReady ? "post-PKI" : "pre-PKI";
  return (
    <header className="view-header">
      <div className="view-header__eyebrow">
        <span className="accent-teal">Cockpit</span>
        <span><SpecRef text="Operator · §8" /></span>
      </div>
      <h1 className="view-header__title">Operator Cockpit</h1>
      <p className="view-header__sub">
        The tiered provisioning surface over the fail-closed CLI. Observe is
        read-only; Declare &amp; Deploy and the Ceremony are reached only behind
        the mTLS-terminating proxy — the panel renders that boundary honestly and
        mints no keys.{" "}
        <span className="mono" style={{ color: pkiReady ? "var(--teal)" : "var(--fg-faint)" }}>
          stage: {stage}
        </span>
      </p>
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* 1 — Observe (Tier-0)                                               */
/* ------------------------------------------------------------------ */

function ObserveSection({ status }: { status: CockpitStatus | null }) {
  const planes = status ? Object.entries(status.planes) : [];

  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <SectionHeading
        tier="Tier 0 · Observe"
        title="Per-plane state"
        note="Read-only, zero authority. The provisioning stage flag and each enabled plane's live state, straight from GET /v1/cockpit/status — no state is asserted the backend did not report."
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
          fontSize: "12.5px",
        }}
      >
        <span className="mono" style={{ color: "var(--fg-faint)" }}>
          node PKI:
        </span>
        {status === null ? (
          <span className="mono" style={{ color: "var(--fg-faint)" }}>
            reading…
          </span>
        ) : (
          <VerifyBadge
            state={status.pki_ready ? "verified" : "pending"}
            label={status.pki_ready ? "PKI established" : "Awaiting PKI"}
            size="sm"
          />
        )}
        <span className="mono" style={{ color: "var(--fg-faint)" }}>
          sealed ACs: <span style={{ color: "var(--fg-muted)" }}>— pending metric</span>
        </span>
      </div>

      {status === null ? (
        <PlaneState kind="loading" title="Reading cockpit status…" />
      ) : planes.length === 0 ? (
        <PlaneState
          kind="empty"
          title="No planes enabled yet"
          message="The declare-zone enables no planes in this manifest, so there is no per-plane state to observe."
          specRef="Design §8.1"
        />
      ) : (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {planes.map(([name, state]) => (
            <li
              key={name}
              className="panel panel--inset"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                padding: "12px 16px",
              }}
            >
              <span style={{ fontWeight: 500, textTransform: "capitalize" }}>{name}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "10px" }}>
                <span className="mono" style={{ fontSize: "12px", color: "var(--fg-faint)" }}>
                  {state}
                </span>
                <VerifyBadge state={planeVerdict(state)} size="sm" />
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 2 — Manifest firewall (declare / generate / pin)                   */
/* ------------------------------------------------------------------ */

function ManifestFirewall({
  manifest,
  pinsReady,
}: {
  manifest: CockpitManifest | null;
  pinsReady: boolean;
}) {
  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      <SectionHeading
        tier="Tier 1 · Declare &amp; Deploy"
        title="Manifest — the declare / generate / pin firewall"
        note="The declare-zone is operator-authored and non-secret, so it is shown verbatim. The generate-zone exposes a thumbprint only — never seed bytes. The pin-zone is presence-only: the two external trust anchors gate the fail-closed Deploy. Editing the manifest is a Tier-1 mTLS-gated action; the panel cannot present a client cert, so the form is a read-only honest projection here."
      />

      {manifest === null ? (
        <PlaneState
          kind="offline"
          title="No manifest projected"
          message="GET /v1/cockpit/manifest returned no document (404 pre-declare, or the backend is mid-provision). The declare-zone is authored out-of-band; this panel renders it once present."
          specRef="Design §8.2"
        />
      ) : (
        <>
          {/* declare-zone — verbatim, read-only summary */}
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <ZoneLabel accent="var(--indigo)" word="declare-zone" note="operator-authored · non-secret · editable behind mTLS" />
            <dl
              style={{
                display: "grid",
                gridTemplateColumns: "max-content 1fr",
                gap: "6px 18px",
                margin: 0,
                fontSize: "13px",
              }}
            >
              <Field label="country" value={manifest.declare.country} />
              <Field label="member_id" value={manifest.declare.node.member_id} mono />
              <Field
                label="members"
                value={
                  manifest.declare.members.length === 0
                    ? "—"
                    : manifest.declare.members
                        .map((m) => `${m.id} (${m.role})`)
                        .join(", ")
                }
              />
              <Field
                label="peers"
                value={
                  manifest.declare.peers.length === 0
                    ? "— none"
                    : manifest.declare.peers.map((p) => `${p.member}@${p.country}`).join(", ")
                }
              />
              <Field
                label="consent"
                value={`channels: ${manifest.declare.consent.channels.join(", ") || "—"} · single-use default: ${manifest.declare.consent.single_use_default}`}
              />
              <Field
                label="planes"
                value={manifest.declare.planes.enabled.join(", ") || "— none enabled"}
                mono
              />
            </dl>
          </div>

          {/* generate-zone — THP only, never the seed */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              borderTop: "1px solid var(--hair)",
              paddingTop: "16px",
            }}
          >
            <ZoneLabel accent="var(--gold)" word="generate-zone" note="read-only thumbprint · the seed never leaves the host" />
            {manifest.generate.emitter_thp ? (
              <Hash value={manifest.generate.emitter_thp} label="emitter THP" accent="var(--gold)" />
            ) : (
              <span className="mono" style={{ fontSize: "12.5px", color: "var(--fg-faint)" }}>
                emitter THP — not yet generated (the <code>provision ceremony</code> verb mints it on the host)
              </span>
            )}
          </div>

          {/* pin-zone — presence-only external trust anchors */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              borderTop: "1px solid var(--hair)",
              paddingTop: "16px",
            }}
          >
            <ZoneLabel accent="var(--teal)" word="pin-zone" note="external trust anchors · presence-only · gates the Deploy" />
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <PinChip
                label="witness genesis pin"
                present={manifest.trust_anchors_present.witness_genesis_pin}
              />
              <PinChip
                label="audit anchor signer THP"
                present={manifest.trust_anchors_present.audit_anchor_signer_thp}
              />
            </div>
            <p
              className="mono"
              style={{ fontSize: "11.5px", color: pinsReady ? "var(--teal)" : "var(--fg-faint)", margin: 0 }}
            >
              {pinsReady
                ? "✓ both external anchors present — the Deploy gate's pin condition is met"
                : "⦸ awaiting at least one external trust anchor — the fail-closed Deploy gate stays shut (spec §8.2)"}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 3 — Ceremony wizard (Tier-2 verify-pins, render-don't-execute)     */
/* ------------------------------------------------------------------ */

function CeremonyWizard({ pkiReady }: { pkiReady?: boolean }) {
  const [genesisPin, setGenesisPin] = useState("");
  const [signerThp, setSignerThp] = useState("");
  const [result, setResult] = useState<VerifyPinsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  // Mirror the backend's fail-closed shape check (64 lower-case hex) so the UX
  // is honest BEFORE the network: a malformed pin can never look acceptable.
  const genesisShapeOk = useMemo(() => /^[0-9a-f]{64}$/.test(genesisPin), [genesisPin]);
  const canVerify = genesisShapeOk && signerThp.trim().length > 0 && !verifying;

  async function onVerify() {
    setError(null);
    setResult(null);
    setVerifying(true);
    try {
      const r = await verifyPins({
        witness_genesis_pin: genesisPin.trim(),
        audit_anchor_signer_thp: signerThp.trim(),
      });
      setResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : "verify-pins failed");
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <SectionHeading
        tier="Tier 2 · Ceremony"
        title="Verify external pins — render, don't execute"
        note="Paste the two PUBLIC commitments (the witness genesis pin and the audit-anchor signer thumbprint). The panel verifies their shape and returns a public-commitments-only transcript. It NEVER holds, mints, or reads a private seed — key generation stays the out-of-band provision ceremony verb on the host."
      />

      <div style={{ display: "flex", flexDirection: "column", gap: "12px", maxWidth: "72ch" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <span className="mono" style={{ fontSize: "12px", color: "var(--fg-muted)" }}>
            witness_genesis_pin <span style={{ color: "var(--fg-faint)" }}>(64 lower-case hex — a SHA-256 commitment)</span>
          </span>
          <input
            value={genesisPin}
            onChange={(e) => setGenesisPin(e.target.value)}
            spellCheck={false}
            autoComplete="off"
            placeholder="e.g. 3a7f…"
            className="mono"
            style={inputStyle(genesisPin.length > 0 && !genesisShapeOk)}
          />
          {genesisPin.length > 0 && !genesisShapeOk && (
            <span className="mono" style={{ fontSize: "11px", color: "var(--red)" }}>
              must be exactly 64 lower-case hex characters (fail-closed before the call)
            </span>
          )}
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <span className="mono" style={{ fontSize: "12px", color: "var(--fg-muted)" }}>
            audit_anchor_signer_thp <span style={{ color: "var(--fg-faint)" }}>(RFC 7638 thumbprint — public)</span>
          </span>
          <input
            value={signerThp}
            onChange={(e) => setSignerThp(e.target.value)}
            spellCheck={false}
            autoComplete="off"
            placeholder="e.g. NzbLsXh8…"
            className="mono"
            style={inputStyle(false)}
          />
        </label>

        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => void onVerify()}
            disabled={!canVerify}
            title={
              canVerify
                ? "POST /v1/cockpit/ceremony/verify-pins (Tier-2, mTLS operator cert)"
                : "Paste a well-formed genesis pin and signer thumbprint to verify"
            }
            style={buttonStyle(!canVerify, "var(--teal)")}
          >
            {verifying ? "Verifying…" : "Verify pins"}
          </button>
          <span className="mono" style={{ fontSize: "11.5px", color: "var(--fg-faint)" }}>
            render-don't-execute · Tier-2 · no seed ever held
          </span>
        </div>

        {error && (
          <PlaneState
            kind="error"
            title="Pin verification failed"
            message={error}
            specRef="Design §8.1"
          />
        )}

        {result && (
          <div
            className="panel panel--inset"
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <VerifyBadge
                state={result.verified ? "verified" : "failed"}
                label={result.verified ? "Pins verified" : "Pins rejected"}
                size="sm"
              />
              <span className="mono" style={{ fontSize: "11.5px", color: "var(--fg-faint)" }}>
                verified at tier {result.transcript.verified_at_tier}
              </span>
            </div>
            <Hash value={result.transcript.witness_genesis_pin} label="genesis pin" accent="var(--teal)" />
            <Hash value={result.transcript.audit_anchor_signer_thp} label="signer THP" accent="var(--gold)" />
          </div>
        )}

        {pkiReady === false && (
          <p className="mono" style={{ fontSize: "11px", color: "var(--fg-faint)", margin: 0 }}>
            note: the node PKI is not yet established — the ceremony verifies the public anchors that the
            out-of-band key generation will pin to.
          </p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 4 — Deploy + step-9 exchange visualizer (Tier-1)                   */
/* ------------------------------------------------------------------ */

function DeployAndExchange({
  role,
  deployEnabled,
  pinsReady,
  pkiReady,
}: {
  role: Role;
  deployEnabled: boolean;
  pinsReady: boolean;
  pkiReady?: boolean;
}) {
  const isOperator = role === "operator";

  // The honest gate explanation: WHY the privileged trigger is disabled. The
  // SPA can never present the client cert the mTLS leg requires, so the button
  // is always disabled-with-explanation; the gate state still reports honestly.
  const gateReason = !isOperator
    ? "Operator role required for the Tier-1 affordance"
    : !pinsReady
      ? "Awaiting external trust anchor — fail-closed Deploy gate (spec §8.2)"
      : !pkiReady
        ? "Node PKI not yet established"
        : "Reached only behind the mTLS-terminating proxy — the browser holds no client cert";

  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      <SectionHeading
        tier="Tier 1 · Declare &amp; Deploy"
        title="Deploy + step-9 exchange"
        note="The deploy self-audits its §38.3 event BEFORE the side effect and aborts 503 without deploying if the seal fails (audit-first). The fail-closed gate opens only when both external pins are present AND the node PKI is established. The panel never deploys itself — it cannot present a client cert; the trigger is disabled-with-explanation."
      />

      <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
        <button
          type="button"
          disabled
          aria-disabled="true"
          title={
            deployEnabled
              ? `POST /v1/cockpit/deploy (mTLS operator cert) — ${gateReason}`
              : gateReason
          }
          style={buttonStyle(true, "var(--teal)")}
        >
          Deploy node…
        </button>
        <VerifyBadge
          state={deployEnabled ? "attested" : "offline"}
          label={deployEnabled ? "Gate satisfied · mTLS pending" : "Gate closed"}
          size="sm"
        />
        <span className="mono" style={{ fontSize: "11.5px", color: "var(--fg-faint)", maxWidth: "60ch" }}>
          {gateReason}
        </span>
      </div>

      {/* step-9 exchange visualizer — the four planes lighting up in hop order */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          borderTop: "1px solid var(--hair)",
          paddingTop: "16px",
        }}
      >
        <ZoneLabel
          accent="var(--ember)"
          word="step-9 exchange"
          note="consent-gated four-plane hop · P3 audits the intent and stubs not_yet_wired · live in P4"
        />
        <ol
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            alignItems: "stretch",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          {EXCHANGE_PLANES.map((p, i) => (
            <li
              key={p.id}
              className="panel panel--inset"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 14px",
                borderColor: pkiReady ? p.accent : "var(--hair)",
                opacity: pkiReady ? 1 : 0.6,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  background: pkiReady ? p.accent : "var(--fg-decor)",
                  flex: "0 0 auto",
                }}
              />
              <span className="mono" style={{ fontSize: "12px" }}>
                {i + 1}. {p.label}
              </span>
            </li>
          ))}
        </ol>
        <span className="mono" style={{ fontSize: "11.5px", color: "var(--fg-faint)" }}>
          POST /v1/cockpit/exchange/run → status "not_yet_wired" in P3; the live consent → trust →
          transport → audit hop is wired in P4.
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Small presentational helpers                                       */
/* ------------------------------------------------------------------ */

function SectionHeading({ tier, title, note }: { tier: string; title: string; note: string }) {
  return (
    <div>
      <div
        className="mono"
        style={{
          fontSize: "11px",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--indigo)",
          marginBottom: "4px",
        }}
      >
        {tier}
      </div>
      <h2 style={{ fontSize: "20px", margin: "0 0 6px" }}>{title}</h2>
      <p style={{ color: "var(--fg-faint)", margin: 0, fontSize: "13px", maxWidth: "82ch" }}>
        {note}
      </p>
    </div>
  );
}

function ZoneLabel({ accent, word, note }: { accent: string; word: string; note: string }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: "10px", flexWrap: "wrap" }}>
      <span
        className="mono"
        style={{
          fontSize: "11px",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: accent,
          border: `1px solid ${accent}`,
          borderRadius: "999px",
          padding: "2px 9px",
        }}
      >
        {word}
      </span>
      <span className="mono" style={{ fontSize: "11px", color: "var(--fg-faint)" }}>
        {note}
      </span>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="mono" style={{ fontSize: "12px", color: "var(--fg-faint)" }}>
        {label}
      </dt>
      <dd
        className={mono ? "mono" : undefined}
        style={{ margin: 0, color: "var(--fg)", overflowWrap: "anywhere" }}
      >
        {value}
      </dd>
    </>
  );
}

function PinChip({ label, present }: { label: string; present: boolean }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        padding: "5px 11px",
        borderRadius: "999px",
        fontFamily: "var(--font-mono)",
        fontSize: "12px",
        color: present ? "var(--teal)" : "var(--fg-faint)",
        border: `1px solid ${present ? "var(--teal)" : "var(--hair-strong)"}`,
        background: present ? "var(--teal-dim)" : "transparent",
      }}
    >
      <span aria-hidden="true">{present ? "✓" : "○"}</span>
      {label}
      <span style={{ color: "var(--fg-faint)" }}>· {present ? "present" : "absent"}</span>
    </span>
  );
}

function inputStyle(invalid: boolean): React.CSSProperties {
  return {
    minHeight: "44px",
    padding: "0 12px",
    borderRadius: "var(--radius-sm)",
    border: `1px solid ${invalid ? "var(--red)" : "var(--hair-strong)"}`,
    background: "var(--bg)",
    color: "var(--fg)",
    fontFamily: "var(--font-mono)",
    fontSize: "13px",
  };
}

function buttonStyle(disabled: boolean, accent: string): React.CSSProperties {
  return {
    minHeight: "44px",
    padding: "0 18px",
    borderRadius: "var(--radius-sm)",
    border: `1px solid ${disabled ? "var(--hair-strong)" : accent}`,
    background: disabled ? "var(--bg)" : "transparent",
    color: disabled ? "var(--fg-faint)" : accent,
    fontFamily: "var(--font-mono)",
    fontSize: "13px",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.7 : 1,
  };
}
